import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (username, password) => {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);
  
  const response = await api.post('/token', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/users/me');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const getUserByManager = async (managerId) => {
  const response = await api.get(`/users/by-manager/${managerId}`);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/users/${userId}`, userData);
  return response.data;
};

// Weeks
export const getWeeks = async () => {
  const response = await api.get('/weeks');
  return response.data;
};

export const getWeek = async (weekId) => {
  const response = await api.get(`/weeks/${weekId}`);
  return response.data;
};

export const createWeek = async (weekData) => {
  const response = await api.post('/weeks', weekData);
  return response.data;
};

export const closeWeek = async (weekId) => {
  const response = await api.put(`/weeks/${weekId}/close`);
  return response.data;
};

export const openWeek = async (weekId) => {
  const response = await api.put(`/weeks/${weekId}/open`);
  return response.data;
};

export const deleteWeek = async (weekId) => {
  const response = await api.delete(`/weeks/${weekId}`);
  return response.data;
};

// Teams
export const getTeams = async (includeInactive = false) => {
  const params = includeInactive ? { include_inactive: true } : {};
  const response = await api.get('/teams', { params });
  return response.data;
};

export const getTeam = async (teamId) => {
  const response = await api.get(`/teams/${teamId}`);
  return response.data;
};

export const createTeam = async (teamData) => {
  const response = await api.post('/teams', teamData);
  return response.data;
};

export const updateTeam = async (teamId, teamData) => {
  const response = await api.put(`/teams/${teamId}`, teamData);
  return response.data;
};

export const deleteTeam = async (teamId) => {
  const response = await api.delete(`/teams/${teamId}`);
  return response.data;
};

export const restoreTeam = async (teamId) => {
  const response = await api.put(`/teams/${teamId}/restore`);
  return response.data;
};

// Managers
export const getManagers = async (includeInactive = false) => {
  const params = includeInactive ? { include_inactive: true } : {};
  const response = await api.get('/managers', { params });
  return response.data;
};

export const getManager = async (managerId) => {
  const response = await api.get(`/managers/${managerId}`);
  return response.data;
};

export const createManager = async (managerData) => {
  const response = await api.post('/managers', managerData);
  return response.data;
};

export const updateManager = async (managerId, managerData) => {
  const response = await api.put(`/managers/${managerId}`, managerData);
  return response.data;
};

export const deleteManager = async (managerId) => {
  const response = await api.delete(`/managers/${managerId}`);
  return response.data;
};

export const restoreManager = async (managerId) => {
  const response = await api.put(`/managers/${managerId}/restore`);
  return response.data;
};

// Categories
export const getCategories = async () => {
  const response = await api.get('/categories');
  return response.data;
};

export const createCategory = async (categoryData) => {
  const response = await api.post('/categories', categoryData);
  return response.data;
};

export const updateCategory = async (categoryId, categoryData) => {
  const response = await api.put(`/categories/${categoryId}`, categoryData);
  return response.data;
};

export const deleteCategory = async (categoryId) => {
  const response = await api.delete(`/categories/${categoryId}`);
  return response.data;
};

// Lead Stages (Этапы лидов)
export const getLeadStages = async () => {
  const response = await api.get('/lead-stages');
  return response.data;
};

export const createLeadStage = async (stageData) => {
  const response = await api.post('/lead-stages', stageData);
  return response.data;
};

export const updateLeadStage = async (stageId, stageData) => {
  const response = await api.put(`/lead-stages/${stageId}`, stageData);
  return response.data;
};

export const deleteLeadStage = async (stageId) => {
  const response = await api.delete(`/lead-stages/${stageId}`);
  return response.data;
};

// Incomes
export const getIncomes = async (weekId = null) => {
  const params = weekId ? { week_id: weekId } : {};
  const response = await api.get('/incomes', { params });
  return response.data;
};

export const getIncomesGrouped = async (weekId) => {
  const response = await api.get('/incomes/grouped', { params: { week_id: weekId } });
  return response.data;
};

export const createIncome = async (incomeData) => {
  const response = await api.post('/incomes', incomeData);
  return response.data;
};

export const deleteIncome = async (incomeId) => {
  const response = await api.delete(`/incomes/${incomeId}`);
  return response.data;
};

export const updateIncome = async (incomeId, incomeData) => {
  const response = await api.put(`/incomes/${incomeId}`, incomeData);
  return response.data;
};

// Salaries
export const getSalaries = async (weekId) => {
  const response = await api.get('/salaries', { params: { week_id: weekId } });
  return response.data;
};

// Expenses
export const getExpenses = async (weekId = null) => {
  const params = weekId ? { week_id: weekId } : {};
  const response = await api.get('/expenses', { params });
  return response.data;
};

export const createExpense = async (expenseData) => {
  const response = await api.post('/expenses', expenseData);
  return response.data;
};

export const deleteExpense = async (expenseId) => {
  const response = await api.delete(`/expenses/${expenseId}`);
  return response.data;
};

// Payments
export const getPayments = async (weekId) => {
  const response = await api.get(`/payments?week_id=${weekId}`);
  return response.data;
};

export const updatePaymentStatus = async (managerId, weekId, status) => {
  const response = await api.put(`/payments/${managerId}?week_id=${weekId}`, { status });
  return response.data;
};

// Leads
export const getLeads = async (stageId = null, managerId = null) => {
  const params = {};
  if (stageId) params.stage_id = stageId;
  if (managerId) params.manager_id = managerId;
  const response = await api.get('/leads', { params });
  return response.data;
};

export const getLeadsGrouped = async () => {
  const response = await api.get('/leads/grouped');
  return response.data;
};

export const createLead = async (leadData) => {
  const response = await api.post('/leads', leadData);
  return response.data;
};

export const updateLead = async (leadId, leadData) => {
  const response = await api.put(`/leads/${leadId}`, leadData);
  return response.data;
};

export const deleteLead = async (leadId) => {
  const response = await api.delete(`/leads/${leadId}`);
  return response.data;
};

// Reports
export const getReports = async (startDate = null, endDate = null, managerId = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (managerId) params.manager_id = managerId;
  const response = await api.get('/reports', { params });
  return response.data;
};

// Daily Reports
export const submitDailyReport = async (reportData) => {
  const response = await api.post('/daily-reports', reportData);
  return response.data;
};

export const getMyReportStatus = async () => {
  const response = await api.get('/daily-reports/my-status');
  return response.data;
};

export const getDailyReports = async (params = {}) => {
  const response = await api.get('/daily-reports', { params });
  return response.data;
};

export const adminCreateDailyReport = async (managerId, date, reportData) => {
  const response = await api.post(`/daily-reports/admin?manager_id=${managerId}&date=${date}`, reportData);
  return response.data;
};

export const updateDailyReport = async (reportId, reportData) => {
  const response = await api.put(`/daily-reports/${reportId}`, reportData);
  return response.data;
};

export const deleteDailyReport = async (reportId) => {
  const response = await api.delete(`/daily-reports/${reportId}`);
  return response.data;
};

export default api;
